import { Store } from '../config';

/**
 * The group's companies and the market each one operates, in one place: the footer prints them,
 * the home page publishes them as schema.org organizations, and both have to say the same thing.
 *
 * The figures come from the group's CEO and were checked against each market's own about page
 * (`/about.php`, `/om-os`, `/over-ons`, `/uber-uns`) and the brand site ondio.com in September
 * 2026. The group structure is Ondio Group AB > Kontorsgiganten AB > the four foreign companies -
 * the country companies hang off the Swedish operating company, not off the group directly.
 */

export type CompanyKey = 'ondioGroup' | 'kontorsgiganten' | 'ondioNo' | 'ondioDk' | 'ondioNl' | 'ondioDe';

export type CompanyIdentifier = {
	/** the register's own name for the number, used as the schema propertyID */
	propertyID: string;
	/** the number as the register writes it */
	value: string;
	/** the register or court it is kept in, where the number alone does not say */
	name?: string;
	/** how the footer prints it, when that differs from the registered form */
	footerValue?: string;
};

export type CompanyAddress = {
	streetAddress: string;
	postalCode: string;
	addressLocality: string;
	/** ISO 3166-1 alpha-2 */
	addressCountry: string;
};

export type Company = {
	/**
	 * Stable absolute schema @id, the same string on every market that mentions the company, so
	 * the parent in Berlin and the store in Stockholm are one node rather than six lookalikes.
	 */
	id: string;
	/** the name the company trades under */
	name: string;
	legalName: string;
	/** earlier names of the same company, store names and legal names alike */
	alternateName?: string[];
	url: string;
	foundingDate: string;
	identifier: CompanyIdentifier;
	vatID?: string;
	/** the registered address, where the group has given one */
	address?: CompanyAddress;
	parent?: CompanyKey;
	/** profiles that belong to this company rather than to the group */
	sameAs?: string[];
};

/**
 * The group's own profiles. They describe the group across all five markets - the LinkedIn page
 * gives the Stockholm head office and lists locations in several countries, and both are the
 * links the brand site ondio.com puts in its footer - so they sit on the group node and are not
 * copied onto the country companies.
 */
export const GROUP_PROFILES = {
	linkedIn: 'https://www.linkedin.com/company/ondio',
	youTube: 'https://www.youtube.com/@ondioglobal',
};

export const companies: Record<CompanyKey, Company> = {
	ondioGroup: {
		// ondiogroup.com is the group's own site and names Ondio Group AB; ondio.com is the brand's
		// site for customers, which lists the five markets with their companies
		id: 'https://ondiogroup.com/#organization',
		name: 'Ondio Group',
		legalName: 'Ondio Group AB',
		url: 'https://ondiogroup.com/',
		foundingDate: '2010-01-21',
		identifier: { propertyID: 'Organisationsnummer', value: '556799-7118', name: 'Bolagsverket' },
		// no address: the group gave none, and the head office LinkedIn shows is the Swedish
		// operating company's, which is not the same claim
		sameAs: [GROUP_PROFILES.linkedIn, GROUP_PROFILES.youTube],
	},
	kontorsgiganten: {
		id: 'https://www.kontorsgiganten.se/#organization',
		name: 'Kontorsgiganten',
		legalName: 'Kontorsgiganten AB',
		url: 'https://www.kontorsgiganten.se/',
		foundingDate: '2005-12-13',
		identifier: { propertyID: 'Organisationsnummer', value: '556693-6273', name: 'Bolagsverket' },
		vatID: 'SE556693627301',
		address: {
			streetAddress: 'Sveavägen 20',
			postalCode: '111 57',
			addressLocality: 'Stockholm',
			addressCountry: 'SE',
		},
		parent: 'ondioGroup',
	},
	ondioNo: {
		id: 'https://ondio.no/#organization',
		name: 'Ondio',
		legalName: 'Ondio AS',
		// the store was Kontorgiganten with one s, the company Kontorsgiganten Norge AS with two
		alternateName: ['Kontorgiganten', 'Kontorsgiganten Norge AS'],
		url: 'https://ondio.no/',
		foundingDate: '2012-09-13',
		identifier: {
			propertyID: 'Organisasjonsnummer',
			value: '998930170',
			name: 'Brønnøysundregistrene',
			footerValue: '998 930 170',
		},
		vatID: '998930170MVA',
		address: {
			streetAddress: 'Postboks 1681 Vika',
			postalCode: '0120',
			addressLocality: 'Oslo',
			addressCountry: 'NO',
		},
		parent: 'kontorsgiganten',
		sameAs: ['https://www.facebook.com/p/Ondio-Norge-61583689643660/'],
	},
	ondioDk: {
		id: 'https://ondio.dk/#organization',
		name: 'Ondio',
		alternateName: ['Worky'],
		legalName: 'Ondio ApS',
		url: 'https://ondio.dk/',
		foundingDate: '2022-04-01',
		identifier: { propertyID: 'CVR', value: '43168622', name: 'Det Centrale Virksomhedsregister' },
		vatID: 'DK43168622',
		address: {
			streetAddress: 'Landgreven 3 st th',
			postalCode: '1301',
			addressLocality: 'København',
			addressCountry: 'DK',
		},
		parent: 'kontorsgiganten',
		sameAs: ['https://www.facebook.com/p/Ondio-Danmark-61583764985385/'],
	},
	ondioNl: {
		id: 'https://ondio.nl/#organization',
		name: 'Ondio',
		legalName: 'Ondio B.V.',
		url: 'https://ondio.nl/',
		foundingDate: '2025-05-15',
		identifier: { propertyID: 'KvK', value: '97279536', name: 'Kamer van Koophandel' },
		vatID: 'NL867983279B01',
		address: {
			streetAddress: 'Herengracht 420',
			postalCode: '1017 BZ',
			addressLocality: 'Amsterdam',
			addressCountry: 'NL',
		},
		parent: 'kontorsgiganten',
	},
	ondioDe: {
		id: 'https://ondio.de/#organization',
		name: 'Ondio',
		legalName: 'Ondio GmbH',
		url: 'https://ondio.de/',
		foundingDate: '2025-06-24',
		identifier: {
			propertyID: 'HRB',
			value: 'HRB 276379 B',
			// the number only identifies the company together with the court that keeps the register
			name: 'Handelsregister, Amtsgericht Charlottenburg',
			footerValue: '276379B',
		},
		vatID: 'DE459068205',
		address: {
			streetAddress: 'Pappelallee 78/79',
			postalCode: '10437',
			addressLocality: 'Berlin',
			addressCountry: 'DE',
		},
		parent: 'kontorsgiganten',
	},
};

export type MarketOrganization = {
	/**
	 * The company that operates this market's store. Its `url` is the market's canonical home
	 * page: the legacy brands still have configs of their own - kontorgiganten.no and worky.dk -
	 * but both answer 308 to the Ondio host, so the canonical site is the one the structured data
	 * describes whichever host rendered the page.
	 */
	company: CompanyKey;
	/** the store's current name, which is the Ondio rebrand everywhere except Sweden */
	name: string;
	/** names the site is also searched for: the domain, and the brand it was sold under before */
	alternateName: string[];
	description: string;
	/** file under /images, pinned per market rather than read from the legacy hosts' configs */
	logo: string;
	email: string;
	telephone: string;
	/** IETF tag for the language customer service answers in */
	language: string;
};

export const markets: Partial<Record<Store, MarketOrganization>> = {
	kgse: {
		company: 'kontorsgiganten',
		// Sweden keeps the Kontorsgiganten name: the rename to Ondio is planned but postponed, so
		// nothing here may present the Swedish store as Ondio
		name: 'Kontorsgiganten',
		alternateName: ['kontorsgiganten.se'],
		description:
			'Kontorsgiganten är en svensk nätbutik för kontorsmaterial, förbrukningsvaror och produkter till arbetsplatsen, för företag, skolor, organisationer och privatpersoner.',
		logo: 'kgse-logo.svg',
		email: 'kundservice@kontorsgiganten.se',
		telephone: '020-15 20 00',
		language: 'sv',
	},
	kgno: {
		company: 'ondioNo',
		name: 'Ondio',
		alternateName: ['ondio.no', 'Kontorgiganten'],
		description:
			'Ondio er en norsk nettbutikk for kontorrekvisita, forbruksvarer og produkter til arbeidsplassen, for bedrifter, organisasjoner, offentlig sektor og privatpersoner.',
		logo: 'ondio-logo.svg',
		email: 'kundeservice@ondio.no',
		telephone: '+47 23 96 25 00',
		language: 'nb',
	},
	kgdk: {
		company: 'ondioDk',
		name: 'Ondio',
		alternateName: ['ondio.dk', 'Worky'],
		description:
			'Ondio er en dansk webshop med kontorartikler, forbrugsvarer og produkter til arbejdspladsen, til virksomheder, skoler, offentlige institutioner og privatpersoner.',
		logo: 'ondio-logo.svg',
		email: 'kundeservice@ondio.dk',
		telephone: '+45 32 24 30 00',
		language: 'da',
	},
	kgnl: {
		company: 'ondioNl',
		name: 'Ondio',
		alternateName: ['ondio.nl'],
		description:
			'Ondio is een Nederlandse webshop voor kantoorartikelen, verbruiksartikelen en producten voor de werkplek, voor bedrijven, scholen, organisaties en particulieren.',
		logo: 'ondio-logo.svg',
		email: 'klantenservice@ondio.nl',
		telephone: '085 107 8200',
		language: 'nl',
	},
	kgde: {
		company: 'ondioDe',
		name: 'Ondio',
		alternateName: ['ondio.de'],
		description:
			'Ondio ist ein deutscher Onlineshop für Bürobedarf, Verbrauchsmaterial und Produkte für den Arbeitsplatz, für Unternehmen, Schulen, Organisationen und Privatkunden.',
		logo: 'ondio-logo.svg',
		email: 'kundenservice@ondio.de',
		telephone: '0800 663 4600',
		language: 'de',
	},
};

export function marketOrganization(store: Store): MarketOrganization | undefined {
	return markets[store];
}

export function companyFor(store: Store): Company | undefined {
	const market = markets[store];
	return market && companies[market.company];
}

/** the company and every owner above it, nearest first */
export function ownershipChain(key: CompanyKey): Company[] {
	const chain: Company[] = [];
	let next: CompanyKey | undefined = key;
	while (next) {
		const company: Company = companies[next];
		chain.push(company);
		next = company.parent;
	}
	return chain;
}

/** the number as the footer prints it, which is not always the form the register uses */
export function footerIdentifier(company: Company): string {
	return company.identifier.footerValue ?? company.identifier.value;
}

export function footerAddress({ address }: Company): string {
	return address ? `${address.streetAddress}, ${address.postalCode} ${address.addressLocality}` : '';
}
